import React from 'react';
import { observer } from 'mobx-react';
import { observable, computed, action } from 'mobx';
import TimelineAdminOverview, { TimelineGroup, getStartOfWeek, addDays } from '../../component/TimelineAdminOverview';
import { Project, ProjectStore } from '../../store/Project';
import { Company } from '../../store/Company';
import { TeamStore } from '../../store/Team';
import { DeveloperAllocation, DeveloperAllocationStore } from '../../store/planning';
import ProjectEditModal from './ProjectEditModal';
import ProjectEditPanel from './ProjectEditPanel';
import CompanyEditModal from '../Company/Edit';
import { Thread } from '../../module/notes/store/Thread';
import { api } from '../../store/Base';
import { t } from '../../i18n';
import { ActionBtn, ACTION_ACCENTS } from './shared/ActionBtn';
import { OutlineIcon } from './shared/OutlineIcon';

const PALETTE = ['#e53e3e', '#38a169', '#3182ce', '#805ad5', '#dd6b20', '#319795', '#d53f8c', '#6b46c1'];

function projectColor(p: any): string {
    if (p.color) return p.color;
    const name = p.name || '';
    let h = 0;
    for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
    return PALETTE[Math.abs(h) % PALETTE.length];
}

@observer
export default class ProjectOverview extends TimelineAdminOverview {
    myFilterKey = 'planning-project-overview';
    title = t('nav.planning.projectOverview', 'Project Overview');

    store = new ProjectStore({ relations: ['company', 'team'], limit: false });
    developerAllocationStore = new DeveloperAllocationStore({ relations: ['entries.project', 'user'], limit: false });

    filters = [
        { type: 'search', label: t('form.search') },
        () => ({
            label: t('project.field.team.label', 'Team'),
            type: 'select',
            name: '.team:in',
            store: new TeamStore(),
            multiple: true,
            remote: true,
            toOption: (team: any) => ({ text: team.name, value: team.id }),
        }),
        // Activity filter: hide stale projects by default (no time-regs recently).
        // `activity_within` is a backend filter: projects with a registration in
        // the last N days, plus projects that never had any (new projects stay visible).
        () => ({
            label: t('planning.filter.activity.label', 'Activity'),
            type: 'radioButtons',
            name: '.activity_within',
            options: [
                { value: '90', text: t('planning.filter.activity.activeQuarter', '3 months') },
                { value: '180', text: t('planning.filter.activity.activeHalfYear', '6 months') },
                { value: '365', text: t('planning.filter.activity.activeYear', '1 year') },
                { value: undefined, text: t('planning.filter.activity.all', 'All') },
            ],
        }),
    ];

    // ── Modal state ──
    @observable editModalOpen = false;
    @observable editingProject: Project | null = null;
    @observable projectPanelOpen = false;
    @observable companyEditModalOpen = false;
    @observable editingCompany: Company | null = null;

    async componentDidMount() {
        // Note: AdminOverview.componentDidMount (bindUrlParams) does NOT run
        // because we override it. Apply the default activity filter ourselves:
        // hide stale projects (no time-registrations in the past year), unless
        // the URL already carries an explicit activity filter.
        const urlParams = new URLSearchParams(window.location.search);
        const activityKey = '.activity_within';
        if (!urlParams.has(activityKey)) {
            (this.store.params as any)[activityKey] = '365';
        } else if (urlParams.get(activityKey) === '') {
            delete (this.store.params as any)[activityKey];
        }
        await this.fetch();
    }

    async fetch() {
        await Promise.all([
            this.store.fetch().catch((e: any) => console.warn('project fetch failed', e)),
            this.developerAllocationStore.fetch().catch((e: any) => console.warn('alloc fetch failed', e)),
        ]);
    }

    // ── Data getters ──
    get projects(): Project[] {
        return (this.store.models as Project[] || []).filter(p => !p.hidden);
    }

    get allocations(): DeveloperAllocation[] {
        return this.developerAllocationStore.models as DeveloperAllocation[] || [];
    }

    getProjectAllocations(project: any) {
        return this.allocations.filter(a => {
            const entries = (a as any).entries?.models || (a as any).entries || [];
            return entries.some((e: any) => {
                const epid = e.project?.id ?? e.project;
                return Number(epid) === Number(project.id);
            });
        });
    }

    getProjectStartDate(p: any): Date {
        const allocs = this.getProjectAllocations(p);
        if (allocs.length > 0 && allocs[0].startDate) return new Date(allocs[0].startDate);
        // No allocations: previously every unallocated project fell back to
        // 'this week', stacking all stale projects onto today's column. Anchor
        // at the last real activity instead — but clamp into the visible window
        // so still-active projects with older activity remain visible.
        const lastActivity = (p as any).lastActivity ?? (p as any).last_activity;
        if (lastActivity) {
            const anchor = new Date(lastActivity);
            const visStart = this.startDate;
            if (anchor < visStart) return visStart;
            return anchor;
        }
        return addDays(getStartOfWeek(new Date()), 0);
    }

    getProjectEndDate(p: any): Date {
        const allocs = this.getProjectAllocations(p);
        if (allocs.length > 0) {
            const ends = allocs.map(a => a.endDate ? new Date(a.endDate) : null).filter(Boolean) as Date[];
            if (ends.length > 0) return new Date(Math.max(...ends.map(d => d.getTime())));
        }
        return addDays(this.getProjectStartDate(p), 56);
    }

    get activeProjects(): Project[] {
        return this.projects.filter(p => {
            const start = this.getProjectStartDate(p);
            const end = this.getProjectEndDate(p);
            return this.isInRange(start, end);
        });
    }

    @computed get timelineGroups(): TimelineGroup[] {
        const active = this.activeProjects;
        const byCustomer = new Map<string, { name: string; projects: Project[]; company: any }>();

        for (const p of active) {
            const company = (p as any).company;
            const custName = company?.name || 'Unassigned';
            const custId = company?.id?.toString() || 'unassigned';
            if (!byCustomer.has(custId)) {
                byCustomer.set(custId, { name: custName, projects: [], company });
            }
            (byCustomer.get(custId) as any).projects.push(p);
        }

        return Array.from(byCustomer.entries()).map(([id, { name, projects, company }]) => ({
            id,
            label: name,
            items: projects,
            // Attach company for edit button
            ...({ company } as any),
        }));
    }

    // Click project bar → open side panel
    onBarClick(item: any) {
        this.openEditProjectModal(item);
    }

    onBarDoubleClick(item: any) {
        const projectId = item.id;
        if (projectId) {
            (this.props as any).history?.push(`/planning/project/${projectId}/overview`);
        }
    }

    getBarStart(item: any): Date | null { return this.getProjectStartDate(item); }
    getBarEnd(item: any): Date | null { return this.getProjectEndDate(item); }
    barColor(item: any): string { return projectColor(item); }
    getBarLabel(item: any): string { return item.name || `#${item.id}`; }

    renderTimelineBar(item: any): React.ReactNode {
        const allocs = this.getProjectAllocations(item);
        return (
            <span style={{ display: 'flex', alignItems: 'center', gap: 4, overflow: 'hidden' }}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {item.name}
                    {allocs.length > 0 && ` · ${allocs.length} dev${allocs.length > 1 ? 's' : ''}`}
                </span>
            </span>
        );
    }

    // Edit button is now rendered inside renderTimelineBar above.
    renderBarEditButton(): React.ReactNode { return null; }

    // ── Edit button on customer group header ──
    renderGroupEditButton(group: any): React.ReactNode {
        // Only show if this is a real company (not "Unassigned")
        if (!group.company?.id) return null;
        return (
            <button
                key="edit"
                data-test-edit-customer={group.company.id}
                onClick={(e) => { e.stopPropagation(); this.openEditCompanyModal(group.company); }}
                style={{
                    background: 'transparent', border: '1px solid #e2e5e9', borderRadius: 3,
                    padding: '1px 6px', fontSize: 10, cursor: 'pointer', color: '#6b7280', marginLeft: 'auto',
                }}
            >
                ✎ Edit
            </button>
        );
    }

    // ── Add project button in toolbar ──
    renderAddButton(): React.ReactNode {
        return (
            <ActionBtn
                data-test-add-project-btn
                onClick={this.openAddProjectModal}
                accent={ACTION_ACCENTS.primary}
            >
                <OutlineIcon name="plus" color={ACTION_ACCENTS.primary} />
                <span>Add Project</span>
            </ActionBtn>
        );
    }

    // ── Modal actions ──
    @action openAddProjectModal = async () => {
        // Create the project with all relations (including commentNoteThread)
        // so __activeCurrentRelations is properly populated by mobx-spine.
        // Without this, toBackend() skips comment_note_thread → backend 400.
        const p = new (Project as any)({}, { relations: ['company', 'team', 'projectLead', 'commentNoteThread'] });
        // Pre-create a comment note thread (required FK by backend)
        try {
            const res = await api.post('thread/', {});
            const threadId = res.data?.id || res.id;
            (p as any).commentNoteThread = new Thread({ id: threadId } as any);
        } catch (e) {
            console.error('Failed to create thread for new project', e);
        }
        this.editingProject = p;
        this.editModalOpen = true;
    };

    @action openEditProjectModal = (project: Project) => {
        this.editingProject = project;
        this.projectPanelOpen = true;
    };
    @action closeProjectPanel = () => { this.projectPanelOpen = false; this.editingProject = null; };
    @action afterProjectPanelChange = async () => { await this.store.fetch(); };

    @action closeEditProjectModal = () => {
        this.editModalOpen = false;
        this.editingProject = null;
    };

    @action openEditCompanyModal = (company: Company) => {
        this.editingCompany = company;
        this.companyEditModalOpen = true;
    };

    @action closeEditCompanyModal = () => {
        this.companyEditModalOpen = false;
        this.editingCompany = null;
    };

    @action afterProjectSave = async () => {
        this.closeEditProjectModal();
        await this.store.fetch();
    };

    @action afterCompanySave = async () => {
        this.closeEditCompanyModal();
        await this.store.fetch();
    };

    // ── Render modals ──
    renderEditModal(): React.ReactNode {
        return (
            <React.Fragment>
                {this.editModalOpen && this.editingProject ? (
                    <ProjectEditModal
                        model={this.editingProject}
                        open={this.editModalOpen}
                        onClose={this.closeEditProjectModal}
                        afterSave={this.afterProjectSave}
                        trigger={<span style={{ display: 'none' }} />}
                    />
                ) : null}
                {this.companyEditModalOpen && this.editingCompany ? (
                    <CompanyEditModal
                        model={this.editingCompany}
                        open={this.companyEditModalOpen}
                        onClose={this.closeEditCompanyModal}
                        afterSave={this.afterCompanySave}
                        trigger={<span style={{ display: 'none' }} />}
                    />
                ) : null}
            </React.Fragment>
        );
    }
    renderSidePanel(): React.ReactNode {
        return (
            <ProjectEditPanel
                project={this.editingProject}
                visible={this.projectPanelOpen}
                allocations={this.allocations}
                onClose={this.closeProjectPanel}
                onChanged={this.afterProjectPanelChange}
                onMilestoneClick={(milestoneId) => {
                    this.closeProjectPanel();
                    (this.props as any).history?.push('/planning/milestone/' + milestoneId + '/overview');
                }}
            />
        );
    }
}
